// Google Calendar Service
// Integração completa com Google Calendar API

import { supabase } from '../lib/supabase';
import { CalendarEvent, Calendar, FreeSlot } from '../types/tools';
import { systemConfigService } from './system-config-service';

const CALENDAR_SCOPES = 'https://www.googleapis.com/auth/calendar https://www.googleapis.com/auth/calendar.events';
const PROVIDER = 'google_calendar';

// Função para obter Client ID (Vault > ENV)
const getGoogleClientId = (): string => {
  const fromVault = systemConfigService.getSync('GOOGLE_CLIENT_ID');
  if (fromVault) return fromVault;
  return import.meta.env.VITE_GOOGLE_CLIENT_ID || '';
};

// Função para obter API Key (Vault > ENV)
const getGoogleApiKey = (): string => {
  const fromVault = systemConfigService.getSync('GOOGLE_API_KEY');
  if (fromVault) return fromVault;
  return import.meta.env.VITE_GOOGLE_API_KEY || '';
};

interface UserIntegration {
  id: string;
  user_id: string;
  provider: string;
  access_token: string;
  refresh_token?: string;
  token_expiry?: string;
  provider_email?: string;
  is_active: boolean;
}

class GoogleCalendarService {
  private accessToken: string | null = null;
  private currentUserId: string | null = null;

  // Carregar token salvo do Supabase
  async loadSavedToken(userId: string): Promise<string | null> {
    try {
      const { data, error } = await supabase
        .from('user_integrations')
        .select('*')
        .eq('user_id', userId)
        .eq('provider', PROVIDER)
        .eq('is_active', true)
        .single();

      if (error || !data) {
        console.log('Nenhum token salvo encontrado para Google Calendar');
        return null;
      }

      // Verificar se o token expirou
      if (data.token_expiry) {
        const expiry = new Date(data.token_expiry);
        if (expiry < new Date()) {
          console.log('Token do Calendar expirado, necessário novo login');
          return null;
        }
      }

      this.accessToken = data.access_token;
      this.currentUserId = userId;
      console.log('✅ Token do Google Calendar carregado do banco');
      return data.access_token;
    } catch (error) {
      console.error('Erro ao carregar token:', error);
      return null;
    }
  }

  // Salvar token no Supabase
  async saveToken(userId: string, accessToken: string, expiresIn?: number): Promise<void> {
    try {
      const tokenExpiry = expiresIn 
        ? new Date(Date.now() + expiresIn * 1000).toISOString()
        : new Date(Date.now() + 3600 * 1000).toISOString();

      const { error } = await supabase
        .from('user_integrations')
        .upsert({
          user_id: userId,
          provider: PROVIDER,
          access_token: accessToken,
          token_expiry: tokenExpiry,
          is_active: true,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id,provider'
        });

      if (error) {
        console.error('Erro ao salvar token:', error);
        throw error;
      }

      console.log('✅ Token do Google Calendar salvo no banco');
    } catch (error) {
      console.error('Erro ao salvar token:', error);
    }
  }

  // Verificar se está conectado
  async isConnected(userId: string): Promise<boolean> {
    if (this.accessToken && this.currentUserId === userId) {
      return true;
    }
    const token = await this.loadSavedToken(userId);
    return !!token;
  }

  // Desconectar
  async disconnect(userId: string): Promise<void> {
    try {
      await supabase
        .from('user_integrations')
        .delete()
        .eq('user_id', userId)
        .eq('provider', PROVIDER);

      this.accessToken = null;
      this.currentUserId = null;
      console.log('✅ Google Calendar desconectado');
    } catch (error) {
      console.error('Erro ao desconectar:', error);
    }
  }

  // Obter token de acesso via popup OAuth
  async getAccessToken(userId: string): Promise<string> {
    // Primeiro, tentar carregar token salvo
    const savedToken = await this.loadSavedToken(userId);
    if (savedToken) {
      return savedToken;
    }

    // Se não tem token salvo, fazer login
    return new Promise((resolve, reject) => {
      const clientId = getGoogleClientId();
      const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
        `client_id=${encodeURIComponent(clientId)}` +
        `&redirect_uri=${encodeURIComponent(window.location.origin)}` +
        `&response_type=token` +
        `&scope=${encodeURIComponent(CALENDAR_SCOPES)}` +
        `&include_granted_scopes=true`;

      const width = 500;
      const height = 600;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      
      const popup = window.open(
        authUrl,
        'google-calendar-auth',
        `width=${width},height=${height},left=${left},top=${top}`
      );

      if (!popup) {
        reject(new Error('Popup bloqueado. Permita popups para este site.'));
        return;
      }

      const checkPopup = setInterval(() => {
        try {
          if (popup.closed) {
            clearInterval(checkPopup);
            reject(new Error('Autenticação cancelada'));
            return;
          }

          const popupUrl = popup.location.href;
          
          if (popupUrl.startsWith(window.location.origin)) {
            clearInterval(checkPopup);
            
            const hash = popup.location.hash;
            popup.close();
            
            if (hash) {
              const params = new URLSearchParams(hash.substring(1));
              const token = params.get('access_token');
              const expiresIn = params.get('expires_in');
              
              if (token) {
                this.accessToken = token;
                this.currentUserId = userId;
                
                // Salvar token no banco
                this.saveToken(userId, token, expiresIn ? parseInt(expiresIn) : undefined);
                
                resolve(token);
              } else {
                reject(new Error('Token não encontrado'));
              }
            } else {
              reject(new Error('Resposta inválida do Google'));
            }
          }
        } catch (e) {
          // Erro de cross-origin esperado enquanto estiver no Google
        }
      }, 500);

      setTimeout(() => {
        clearInterval(checkPopup);
        if (!popup.closed) {
          popup.close();
        }
        reject(new Error('Timeout na autenticação'));
      }, 120000);
    });
  }

  // ==================== OPERAÇÕES DO CALENDAR ====================

  // Listar calendários
  async listCalendars(userId: string): Promise<Calendar[]> {
    const token = await this.getAccessToken(userId);
    
    const response = await fetch(
      'https://www.googleapis.com/calendar/v3/users/me/calendarList',
      {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    );

    if (!response.ok) {
      throw new Error(`Erro ao listar calendários: ${response.statusText}`);
    }

    const data = await response.json();
    return data.items.map((cal: any) => ({
      id: cal.id,
      summary: cal.summary,
      description: cal.description,
      primary: cal.primary,
      backgroundColor: cal.backgroundColor
    }));
  }

  // Listar eventos
  async listEvents(
    userId: string,
    options: {
      calendarId?: string;
      timeMin?: string;
      timeMax?: string;
      maxResults?: number;
      query?: string;
    } = {}
  ): Promise<CalendarEvent[]> {
    const token = await this.getAccessToken(userId);
    const calendarId = options.calendarId || 'primary';
    
    const params = new URLSearchParams();
    
    if (options.timeMin) {
      params.append('timeMin', new Date(options.timeMin).toISOString());
    } else {
      params.append('timeMin', new Date().toISOString());
    }
    
    if (options.timeMax) {
      params.append('timeMax', new Date(options.timeMax).toISOString());
    }
    
    params.append('maxResults', String(options.maxResults || 10));
    params.append('singleEvents', 'true');
    params.append('orderBy', 'startTime');
    
    if (options.query) {
      params.append('q', options.query);
    }

    const response = await fetch(
      `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(calendarId)}/events?${params}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    );

    if (!response.ok) {
      throw new Error(`Erro ao listar eventos: ${response.statusText}`);
    }

    const data = await response.json();
    return data.items.map((event: any) => ({
      id: event.id,
      summary: event.summary,
      description: event.description,
      start: event.start,
      end: event.end,
      attendees: event.attendees,
      location: event.location,
      htmlLink: event.htmlLink
    }));
  }

  // Criar evento
  async createEvent(
    userId: string,
    event: {
      summary: string;
      description?: string;
      start: string;
      end: string;
      attendees?: string[];
      location?: string;
      calendarId?: string;
    }
  ): Promise<CalendarEvent> {
    const token = await this.getAccessToken(userId);
    const calendarId = event.calendarId || 'primary';

    const eventBody: any = {
      summary: event.summary,
      description: event.description,
      start: {
        dateTime: new Date(event.start).toISOString(),
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
      },
      end: {
        dateTime: new Date(event.end).toISOString(),
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
      }
    };

    if (event.location) {
      eventBody.location = event.location;
    }

    if (event.attendees && event.attendees.length > 0) {
      eventBody.attendees = event.attendees.map(email => ({ email }));
    }

    const response = await fetch(
      `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(calendarId)}/events?sendUpdates=all`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(eventBody)
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(`Erro ao criar evento: ${error.error?.message || response.statusText}`);
    }

    const data = await response.json();
    return {
      id: data.id,
      summary: data.summary,
      description: data.description,
      start: data.start,
      end: data.end,
      attendees: data.attendees,
      location: data.location,
      htmlLink: data.htmlLink
    };
  }

  // Atualizar evento
  async updateEvent(
    userId: string,
    eventId: string,
    updates: {
      summary?: string;
      description?: string;
      start?: string;
      end?: string;
      attendees?: string[];
      location?: string;
      calendarId?: string;
    }
  ): Promise<CalendarEvent> {
    const token = await this.getAccessToken(userId);
    const calendarId = updates.calendarId || 'primary';

    // Primeiro, buscar o evento atual
    const getResponse = await fetch(
      `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(calendarId)}/events/${eventId}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    );

    if (!getResponse.ok) {
      throw new Error(`Evento não encontrado: ${getResponse.statusText}`);
    }

    const currentEvent = await getResponse.json();

    // Montar o corpo da atualização
    const eventBody: any = {
      ...currentEvent,
      summary: updates.summary ?? currentEvent.summary,
      description: updates.description ?? currentEvent.description,
      location: updates.location ?? currentEvent.location
    };

    if (updates.start) {
      eventBody.start = {
        dateTime: new Date(updates.start).toISOString(),
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
      };
    }

    if (updates.end) {
      eventBody.end = {
        dateTime: new Date(updates.end).toISOString(),
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
      };
    }

    if (updates.attendees) {
      eventBody.attendees = updates.attendees.map(email => ({ email }));
    }

    const response = await fetch(
      `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(calendarId)}/events/${eventId}?sendUpdates=all`,
      {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(eventBody)
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(`Erro ao atualizar evento: ${error.error?.message || response.statusText}`);
    }

    const data = await response.json();
    return {
      id: data.id,
      summary: data.summary,
      description: data.description,
      start: data.start,
      end: data.end,
      attendees: data.attendees,
      location: data.location,
      htmlLink: data.htmlLink
    };
  }

  // Deletar evento
  async deleteEvent(
    userId: string,
    eventId: string,
    calendarId: string = 'primary'
  ): Promise<boolean> {
    const token = await this.getAccessToken(userId);

    const response = await fetch(
      `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(calendarId)}/events/${eventId}?sendUpdates=all`,
      {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    );

    if (!response.ok && response.status !== 204) {
      throw new Error(`Erro ao deletar evento: ${response.statusText}`);
    }

    return true;
  }

  // Encontrar horários livres
  async findFreeSlots(
    userId: string,
    options: {
      attendees?: string[];
      duration?: number; // minutos
      timeMin?: string;
      timeMax?: string;
    }
  ): Promise<FreeSlot[]> {
    const token = await this.getAccessToken(userId);

    // Validar e definir defaults
    const attendees = options.attendees && options.attendees.length > 0 
      ? options.attendees 
      : ['primary'];
    const duration = options.duration || 60;
    const timeMin = options.timeMin || new Date().toISOString();
    const timeMax = options.timeMax || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(); // 7 dias

    const response = await fetch(
      'https://www.googleapis.com/calendar/v3/freeBusy',
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          timeMin: new Date(timeMin).toISOString(),
          timeMax: new Date(timeMax).toISOString(),
          items: attendees.map(email => ({ id: email }))
        })
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`Erro ao buscar disponibilidade: ${errorData?.error?.message || response.statusText}`);
    }

    const data = await response.json();

    // Processar os blocos ocupados de todos os participantes
    const busySlots: { start: Date; end: Date }[] = [];
    
    // Verificar se calendars existe antes de iterar
    if (data.calendars && typeof data.calendars === 'object') {
      for (const calendar of Object.values(data.calendars) as any[]) {
        if (calendar && calendar.busy) {
          for (const busy of calendar.busy) {
            busySlots.push({
              start: new Date(busy.start),
              end: new Date(busy.end)
            });
          }
        }
      }
    }

    // Ordenar por início
    busySlots.sort((a, b) => a.start.getTime() - b.start.getTime());

    // Encontrar slots livres
    const freeSlots: FreeSlot[] = [];
    let currentStart = new Date(timeMin);
    const endTime = new Date(timeMax);
    const durationMs = duration * 60 * 1000;

    // Mesclar slots ocupados que se sobrepõem
    const mergedBusy: { start: Date; end: Date }[] = [];
    for (const slot of busySlots) {
      if (mergedBusy.length === 0) {
        mergedBusy.push({ ...slot });
      } else {
        const last = mergedBusy[mergedBusy.length - 1];
        if (slot.start <= last.end) {
          last.end = new Date(Math.max(last.end.getTime(), slot.end.getTime()));
        } else {
          mergedBusy.push({ ...slot });
        }
      }
    }

    // Encontrar slots livres entre os ocupados
    for (const busy of mergedBusy) {
      if (busy.start > currentStart) {
        const freeEnd = busy.start;
        const freeDuration = freeEnd.getTime() - currentStart.getTime();
        
        if (freeDuration >= durationMs) {
          // Dividir em slots do tamanho solicitado
          let slotStart = currentStart;
          while (slotStart.getTime() + durationMs <= freeEnd.getTime()) {
            freeSlots.push({
              start: slotStart.toISOString(),
              end: new Date(slotStart.getTime() + durationMs).toISOString()
            });
            slotStart = new Date(slotStart.getTime() + durationMs);
          }
        }
      }
      currentStart = new Date(Math.max(currentStart.getTime(), busy.end.getTime()));
    }

    // Verificar slot final
    if (currentStart < endTime) {
      const freeDuration = endTime.getTime() - currentStart.getTime();
      if (freeDuration >= durationMs) {
        let slotStart = currentStart;
        while (slotStart.getTime() + durationMs <= endTime.getTime()) {
          freeSlots.push({
            start: slotStart.toISOString(),
            end: new Date(slotStart.getTime() + durationMs).toISOString()
          });
          slotStart = new Date(slotStart.getTime() + durationMs);
        }
      }
    }

    // Limitar resultados
    return freeSlots.slice(0, 10);
  }

  // Verificar se está configurado
  isConfigured(): boolean {
    const clientId = getGoogleClientId();
    const apiKey = getGoogleApiKey();
    return !!(clientId && apiKey);
  }
}

export const googleCalendarService = new GoogleCalendarService();

